import React from "react";
import { Flex } from "../../../components/Flex";
import { ReportFilters } from "./ReportFilters";

import styled from "styled-components";
import { Spacing } from "../../../theme/Spacing";
import { useTranslation } from "react-i18next";
import { AtiraTabs } from "../../../components/AtiraTabs";
import { ReportsSummaryEntryTab } from "./tabs/summary/ReportsSummaryEntryTab";
import { ReportsSummaryDealTab } from "./tabs/summary/ReportsSummaryDealTab";
import { ReportsSummaryTaskTab } from "./tabs/summary/ReportsSummaryTaskTab";
import { ReportsSummaryUserTab } from "./tabs/summary/ReportsSummaryUserTab";
import { AtiraIcon } from "../../../components/AtiraIcon";
import { EntityIcons } from "../../../utils/GlobalIcons";
import { faUser } from "@fortawesome/free-solid-svg-icons/faUser";
import { ReportsSummaryModuleTab } from "./tabs/summary/ReportsSummaryModuleTab";
import { useAppSelector } from "../../../redux/store";
import { reportSliceSelectors } from "../../../redux/report/report.selector";
import { Info } from "../../../components/Info";
import { Spinner } from "../../../components/Spinner";
import { ReportInsertionTrendChart } from "./ReportInsertionTrendChart";

const StyledTabs = styled(AtiraTabs)`
  padding-inline: 0;
  padding-top: ${Spacing.s};
  height: 100%;

  .ant-tabs-content,
  .ant-tabs-tabpane-active {
    height: 100%;
  }

  .ant-tabs-nav::before {
    display: none;
  }

  .ant-tabs-tab {
    flex-grow: 1;
    justify-content: center;
  }
`;

/**
 * Dims the figures while a new range is loading. Without it the page keeps
 * showing the previous range's numbers with nothing to say they are stale.
 */
const Content = styled(Flex)<{ loading?: boolean }>`
  flex-direction: column;
  transition: opacity 0.15s ease;
  opacity: ${({ loading }) => (loading ? 0.45 : 1)};
  pointer-events: ${({ loading }) => (loading ? "none" : "auto")};
  gap: ${Spacing.m};
`;

const SpinnerRow = styled(Flex)`
  align-items: center;
  justify-content: center;
  gap: ${Spacing.s};
  min-height: 2rem;
`;

export const ReportsSummary: React.FC = () => {
  const { t } = useTranslation();

  const loading = useAppSelector(reportSliceSelectors.selectReportLoading);
  const truncated = useAppSelector(
    reportSliceSelectors.selectReport,
  )?.truncated;

  return (
    <Flex flexDirection="column" gap="l">
      <ReportFilters />

      {loading ? (
        <SpinnerRow>
          <Spinner />
        </SpinnerRow>
      ) : null}

      {truncated ? (
        <Info color="warning" info={t("reports.truncated")} />
      ) : null}

      <Content loading={loading}>
        <ReportInsertionTrendChart />

        <StyledTabs
          items={[
            {
              key: "entry",
              label: (
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={EntityIcons.Entries} />
                  {t("common.entries")}
                </Flex>
              ),
              children: <ReportsSummaryEntryTab />,
            },
            {
              key: "deals",
              label: (
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={EntityIcons.Deals} />
                  {t("common.deals")}
                </Flex>
              ),
              children: <ReportsSummaryDealTab />,
            },
            {
              key: "tasks",
              label: (
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={EntityIcons.Tasks} />
                  {t("common.tasks")}
                </Flex>
              ),
              children: <ReportsSummaryTaskTab />,
            },
            {
              key: "modules",
              label: (
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={EntityIcons.Modules} />
                  {t("common.modules")}
                </Flex>
              ),
              children: <ReportsSummaryModuleTab />,
            },
            {
              key: "user",
              label: (
                <Flex alignItems="center" gap="s">
                  <AtiraIcon icon={faUser} />
                  {t("common.users")}
                </Flex>
              ),
              children: <ReportsSummaryUserTab />,
            },
          ]}
        />
      </Content>
    </Flex>
  );
};
